import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { CheckCircle, AlertCircle, Loader2, Copy, ExternalLink } from 'lucide-react';
import { runDatabaseMigration, checkMigrationStatus, getSQLScript } from '../utils/databaseMigration';

const DatabaseMigration: React.FC = () => {
  const [migrationStatus, setMigrationStatus] = useState<{
    needsMigration: boolean;
    reason: string;
  } | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    setIsChecking(true);
    try {
      const status = await checkMigrationStatus();
      setMigrationStatus(status);
    } catch (error) {
      console.error('Error checking migration status:', error);
      setMigrationStatus({
        needsMigration: true,
        reason: 'Error checking database status'
      });
    } finally {
      setIsChecking(false);
    }
  };

  const runMigration = async () => {
    setIsRunning(true);
    setResult(null);
    
    try {
      const migrationResult = await runDatabaseMigration();
      setResult(migrationResult);
      
      if (migrationResult.success) {
        // Recheck status after successful migration
        setTimeout(() => {
          checkStatus();
        }, 1000);
      }
    } catch (error) {
      setResult({
        success: false,
        message: `Migration failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsRunning(false);
    }
  };

  if (isChecking) {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            Checking Database Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p>Checking if database migration is needed...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {migrationStatus?.needsMigration ? (
            <AlertCircle className="h-5 w-5 text-yellow-500" />
          ) : (
            <CheckCircle className="h-5 w-5 text-green-500" />
          )}
          Database Migration Tool
        </CardTitle>
        <CardDescription>
          Fix the 7 database errors by creating missing tables and columns
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {migrationStatus && (
          <Alert className={migrationStatus.needsMigration ? 'border-yellow-500' : 'border-green-500'}>
            <AlertDescription>
              <strong>Status:</strong> {migrationStatus.reason}
            </AlertDescription>
          </Alert>
        )}

        {migrationStatus?.needsMigration && (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <h4 className="font-semibold mb-2 text-yellow-800">⚠️ Manual SQL Execution Required</h4>
              <p className="text-sm text-yellow-700 mb-3">
                Due to security restrictions, the database schema changes must be executed manually in Supabase.
              </p>
              <div className="space-y-2">
                <h5 className="font-medium text-yellow-800">Steps to fix the 7 errors:</h5>
                <ol className="list-decimal list-inside space-y-1 text-sm text-yellow-700">
                  <li>Copy the SQL script below</li>
                  <li>Go to your Supabase Dashboard</li>
                  <li>Navigate to SQL Editor</li>
                  <li>Paste and run the script</li>
                  <li>Come back and click "Initialize Data"</li>
                </ol>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-semibold">SQL Migration Script</h4>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(getSQLScript());
                    alert('SQL script copied to clipboard!');
                  }}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy Script
                </Button>
              </div>
              <pre className="bg-white p-3 rounded border text-xs overflow-x-auto max-h-40">
                <code>{getSQLScript()}</code>
              </pre>
            </div>

            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => window.open('https://supabase.com/dashboard', '_blank')}
                className="flex-1"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open Supabase Dashboard
              </Button>
              
              <Button 
                onClick={runMigration} 
                disabled={isRunning}
                className="flex-1"
              >
                {isRunning ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Initializing Data...
                  </>
                ) : (
                  'Initialize Data'
                )}
              </Button>
            </div>
          </div>
        )}

        {!migrationStatus?.needsMigration && (
          <div className="text-center py-4">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
            <p className="text-green-600 font-semibold">Database is up to date!</p>
            <p className="text-sm text-gray-600">No migration needed.</p>
          </div>
        )}

        {result && (
          <Alert className={result.success ? 'border-green-500' : 'border-red-500'}>
            <AlertDescription>
              <strong>{result.success ? 'Success:' : 'Error:'}</strong> {result.message}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={checkStatus}
            disabled={isChecking}
            className="flex-1"
          >
            {isChecking ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Checking...
              </>
            ) : (
              'Recheck Status'
            )}
          </Button>
          
          <Button 
            variant="outline" 
            onClick={() => window.location.reload()}
            className="flex-1"
          >
            Refresh App
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default DatabaseMigration;